import { useCallback } from "react";
import { z } from "zod";
import {
  Box,
  Skeleton,
  WidgetEmpty,
  type WidgetModule,
  type WidgetRenderContext,
} from "@salestrak/ui";
import type { WidgetTag } from "#app/widgets/dashboards/widget-tags";
import { BaseItem } from "#app/components/issue-view/activity/base-item";
import { EntityTag } from "#app/components/entity";
import { useIssueView } from "#app/components/issue-view";
import { useActivityList, type ActivityFeedRow } from "./use-activity-list";

/**
 * `activity-list` — the Add-card "Activity" feed: the scoped workspace activity
 * timeline rendered through the SAME `BaseItem` row the issue view uses, so every
 * actor/stage/assignee glyph routes through `#app/components/entity` and reads exactly
 * like a project's own history — never a hand-rolled feed. Each row carries its
 * parent-issue chip and opens the issue view on press; rows that reference a note
 * land on the activity tab focused on that note, the same deep link the inbox
 * uses. Reads `useActivityList` (the activity-shaped scope fold), which lazily
 * pages the WHOLE scoped feed — scrolling near the end appends the next server
 * page, so every entry is reachable and nothing is a capped chunk. Team
 * dashboards narrow to that team's actions; a workflow board folds its pinned
 * entity through the issue side (the activity schema has no `workflowId`).
 */
export const activityListConfigSchema = z.looseObject({});

export type ActivityListConfig = z.infer<typeof activityListConfigSchema>;

/** The note a pressed row lands on: a created/edited note, a visit note, or
 *  the reply a noteReply changeset references. Deleted notes have no target. */
function resolveFocusNoteId(activity: ActivityFeedRow): string | null {
  const { note, age, noteReply } = activity.changeset ?? {};
  if (note?.after) return note.after as string;
  if (age?.after?.noteId) return age.after.noteId as string;
  return Object.keys(noteReply ?? {})[0] ?? null;
}

function ActivityListRender(_ctx: WidgetRenderContext<ActivityListConfig>) {
  const { rows, loading, loadingMore, loadMore } = useActivityList();
  const { openIssueById } = useIssueView();

  const handlePress = useCallback(
    (activity: ActivityFeedRow) => {
      const focusNoteId = resolveFocusNoteId(activity);
      openIssueById(
        activity.issueId,
        focusNoteId ? { initialTab: "activity", focusNoteId } : undefined,
      );
    },
    [openIssueById],
  );

  // Lazy paging: nearing the end of the scroll appends the next server page
  // (`loadMore` self-guards against re-fires and exhaustion).
  const handleScroll = useCallback<React.UIEventHandler<HTMLDivElement>>(
    (event) => {
      const el = event.currentTarget;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 300) loadMore();
    },
    [loadMore],
  );

  if (loading) {
    return (
      <Box fill="flex" gap="sm" padding="sm">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-4 w-full" />
        ))}
      </Box>
    );
  }
  if (rows.length === 0) {
    return <WidgetEmpty icon="activity" title="No activity in range" />;
  }

  return (
    // Plain overflow container, not a ScrollArea: the feed OWNS its scroller —
    // `handleScroll` reads `scrollTop`/`scrollHeight` off this element to page.
    // A plain scroller carries no edge fade.
    <Box fill="flex" overflow="auto" onScroll={handleScroll}>
      <Box padding="sm">
        {rows.map((activity, index) => (
          <Box
            key={`${activity.activityId}-${index}`}
            as="button"
            direction="row"
            align="start"
            gap="sm"
            rounded="sm"
            paddingX="xs"
            hoverBackground
            onClick={() => handlePress(activity)}
          >
            <Box flex={1} minWidth={0}>
              <BaseItem
                activity={activity}
                issueId={activity.issueId}
                last={index === rows.length - 1}
              />
            </Box>
            {activity.issue ? (
              <Box maxWidth="40%" minWidth={0}>
                {/* Inert: the row owns navigation, mirroring the inbox card. */}
                <EntityTag
                  entity={{
                    kind: "issue",
                    id: activity.issue.issueId,
                    issue: activity.issue,
                  }}
                  size="xs"
                  mutedLabel
                  interactive={false}
                />
              </Box>
            ) : null}
          </Box>
        ))}
        {loadingMore ? (
          <Box paddingX="xs" paddingY="sm">
            <Skeleton className="h-4 w-full" />
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

export const activityListDef: WidgetModule<ActivityListConfig> = {
  type: "activity-list",
  title: "Activity",
  icon: "activity",
  // The feed owns its scroll viewport: let its scrollbar meet the card edge and
  // header divider while the inner Box keeps the activity rows comfortably inset.
  flushBody: true,
  span: { minW: 3, minH: 3, maxW: 12, maxH: 12, defaultW: 4, defaultH: 5 },
  configSchema: activityListConfigSchema,
  catalog: [
    {
      key: "activity-list.default",
      label: "Activity",
      description: "A live feed of the latest actions and notes across the projects in scope.",
      section: "Table",
      tags: ["projects"] satisfies readonly WidgetTag[],
      defaultConfig: {},
      defaultW: 4,
      defaultH: 5,
    },
  ],
  render: ActivityListRender,
};
